/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import isUndefined from 'lodash.isundefined';
import getVersion from '@ion/core/src/environment/version';
import * as logger from '@ion/core/src/logger/logger';

/**
 * Provides the static, pre-defined global props that can be assigned values
 * for an application's `globalProps` configuration, and subsequently referenced
 * by components within the DLS package and above via the `getGlobalProp`
 * function.
 *
 * Prop `keys` should provide the context of the prop for which they represent.
 * For example, consider the `modules` property of the `AdminFeatureItem` component.
 * Should this prop need to be defined as a Global Prop, the prop name should be
 * defined as `AdminFeatureItem.Modules`. As such, props should represent the
 * component and prop to which they are associated, denoted as {Component.Prop},
 * and should be defined in PascalCase.
 */
export type GlobalProps = {
  'Application.Name': string;
  'Application.Version': string;
  'DevicesGrid.usePreferredDateTimeFormater': boolean;
  'DevicesGrid.anySearchForOnlyDisplayedColumns': boolean;
  'NotificationEditor.includeSnoozeStepper': boolean;
  'NotificationEditor.useLegacyTemplateUpdate': boolean
  'GlobalGroupChooser.desktop.useDrillDownHierarchy': boolean;
  'grids.export.validation.enabled': boolean;
  'grid.column.search.enabled': boolean;
  'grid.column.order.enabled': boolean;
  'modularSearch.chip.hover.style': boolean;
};

/**
 * Defines the `GlobalProp` type which is based on the pre-defined `GlobalProps`
 * `keys`.
 */
export type GlobalProp = keyof GlobalProps;

/**
 * Defines the `GlobalProps` configuration which contains each `GlobalProp`
 * and it's assigned value for an application. This object is initialized
 * during module load, and can only be assigned application values once
 * via the `setGlobalProps` function (during application initialization).
 *
 * By default, 'Application.Version' is provided via the `getVersion` API
 * if available, otherwise, an empty object is assigned as the default.
 */
const globalProps: Partial<GlobalProps> = (() => {
  const version = getVersion();
  if (version) {
    return {
      'Application.Version' : version
    };
  }
  return {};
})();

/**
 * Provides a reference to the Global Props initialization state. By default, the
 * initialization state will remain `false` until `setGlobalProps` is called, after
 * which it will remain `true` for the duration of the session.
 */
let _initialized = false;

/**
 * The `setGlobalProps` function provides the single point of entry for initializing
 * static global application props. This function should only be invoked once and
 * called automatically via an import within a pre-initialization module. Subsequent
 * calls to this function will simply return `false` and log a warning.
 *
 * The `GlobalPropsConfig` module allows for referencing application specific prop
 * values statically from anywhere within the application's component hierarchy,
 * thus eliminating the need for n-levels of prop drilling for values which are
 * known at design time.
 *
 * Applications should implement a dedicated `GlobalProps.ts` module which invokes
 * this function with the pre-defined global props of interest and their values for
 * the application.
 *
 * For example, a typical `GlobalProps` module for an application would be implemented
 * as follows:
 *
 * ```typescript
 *   // packages/<ion-app-name>/src/props/GlobalProps.ts
 *   import {setGlobalProps} from '@ion/dls/src/props';
 *
 *   setGlobalProps({
 *     ...
 *   });
 * ```
 *
 * From within a module that is loaded during pre-initialization (typically, the
 * `initialization.ts` actions module), the `GlobalProps` module can be imported
 * and thus `setGlobalProps` will automatically be invoked.
 *
 * ```typescript
 *   // /src/store/actions/initialization.ts
 *   ...
 *   import from '../../props/GlobalProps';
 * ```
 *
 * Note: Since `GlobalProps` are to be defined at design-time, and initialized once
 * during pre-initialization, the call to `setGlobalProps` should always be made
 * during module loading.
 *
 * @param   props The `GlobalProps` of interest to the application which are to be
 *          set.
 * @returns `true` if the global props have not previously been initialized,
 *          otherwise, returns `false`.
 */
export const setGlobalProps = (props: Partial<GlobalProps>, log = false): boolean => {
  const getName = () => getGlobalProp('Application.Name', 'Application');
  if (!_initialized) {
    Object.entries(props).forEach(([prop, value]) => {
      globalProps[prop] = value;
    });
    if (log) {
      logger.info(`GlobalProps have been set for ${getName()}`);
      logger.log(globalProps);
    }
    return (_initialized = true);
  }
  logger.warn(`GlobalProps have already been set for ${getName()}, bypassing call`);
  return false;
};

/**
 * Provides access to a given `GlobalProp` value from the application's `GlobalProps`
 * configuration.
 *
 * ```typescript
 *   import getGlobalProp from '@ion/dls/src/props';
 *
 *   // retrieve the value for a specific global prop ...
 *   const name: string = getGlobalProp('Application.Name');
 * ```
 *
 * Clients can provide a fallback value in the event the given global prop has not
 * been set by the application. Typically, this fallback value should correspond
 * to a component prop or state.
 *
 * ```typescript
 *  import getGlobalProp from '@ion/dls/src/props';
 *
 *  const someFunction = () => {
 *    const zoom: number = getGlobalProp('SomeContext.Zoom', 1);
 *    ...
 *  };
 * ```
 *
 * @param   prop The `GlobalProp` for which the value is to be retrieved.
 * @param   fallback An optional `fallback` value to be used in the event the
 *          prop has not been assigned a value by the application.
 * @returns The value of the given `GlobalProp`. The function should be invoked
 *          with the type for which the value is to be returned via the provided
 *          generic.
 */
export const getGlobalProp = <K extends GlobalProp>(
  prop: K,
  fallback?: GlobalProps[K]
): GlobalProps[K] => {
  const value = globalProps[prop] as GlobalProps[K];
  if (isUndefined(value) && !isUndefined(fallback)) {
    return fallback;
  }
  return value;
};

export default getGlobalProp;
